import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, ChevronRight, Map as MapIcon, Sparkles, Lightbulb, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { usePostHog } from "@posthog/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ENTITY_COLORS, ENTITY_TEXT, ENTITY_LABELS } from "@/lib/entityColors";
import { setDescription, DEFAULT_DESCRIPTION } from "@/lib/seo";
import { NodeFields, LearnerActions } from "@/components/explore/DetailPanelParts";
import { Markdown } from "@/components/kg/Markdown";
import { ResearchPanel } from "@/components/ai/ResearchPanel";
import { SuggestEntityDialog } from "@/components/journey/SuggestEntityDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PLURAL = {
  knowledge_system: "Knowledge Systems",
  school: "Schools",
  concept: "Concepts",
  framework: "Frameworks",
  text: "Texts",
  person: "Thinkers",
};

const DESC_KEYS = ["description", "essence", "core_meaning", "summary", "overview"];

export function ConnectionsHub({ nodeId, onOpenNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [, setSearchParams] = useSearchParams();

  const [schema, setSchema] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [learnedIds, setLearnedIds] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [researchOpen, setResearchOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [insertText, setInsertText] = useState(null);
  const [insertFieldKey, setInsertFieldKey] = useState("");

  useEffect(() => {
    api.get("/kg/schema").then((res) => setSchema(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setLearnedIds([]); return; }
    api.get("/progress").then((res) => setLearnedIds(res.data)).catch(() => {});
  }, [user, refresh]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    setDetail(null);
    window.scrollTo?.({ top: 0 });
    api
      .get(`/kg/nodes/${nodeId}`)
      .then((res) => { if (alive) setDetail(res.data); })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nodeId, refresh]);

  const node = detail?.node;
  const type = node?.type;
  const data = node?.data || {};

  useEffect(() => {
    if (node) {
      document.title = `${node.label} — Living Wisdom Explorer`;
      const raw = DESC_KEYS.map((k) => data[k]).find((v) => typeof v === "string" && v.trim());
      setDescription(
        raw
          ? raw.replace(/\s+/g, " ").trim().slice(0, 155)
          : `${node.label} — explore this ${ENTITY_LABELS[node.type] || "topic"} in ancient Indian philosophy, its core ideas and connections.`
      );
      posthog?.capture("entity_viewed", { entity_id: node.id, entity_type: node.type, entity_label: node.label });
    }
    return () => setDescription(DEFAULT_DESCRIPTION);
  }, [node, data, posthog]);

  const fields = useMemo(() => schema?.node_types?.[type]?.fields || [], [schema, type]);
  const nameKey = type === "text" ? "title" : "name";
  const descField = useMemo(
    () => fields.find((f) => DESC_KEYS.includes(f.key) && data[f.key]),
    [fields, data]
  );
  const bodyFields = fields.filter(
    (f) =>
      f.key !== nameKey &&
      f.key !== descField?.key &&
      (Array.isArray(data[f.key]) ? data[f.key].length : data[f.key])
  );

  const edges = detail?.edges || [];
  const groups = useMemo(() => {
    const g = {};
    edges.forEach((e) => {
      const isOut = e.source_id === nodeId;
      const other = {
        id: isOut ? e.target_id : e.source_id,
        label: isOut ? e.target_label : e.source_label,
        otype: isOut ? e.target_type : e.source_type,
        stance: e.props?.stance,
        edgeId: e.id,
      };
      const key = e.label || e.type;
      (g[key] ||= []).push(other);
    });
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [edges, nodeId]);

  const isCurator = user && (user.role === "curator" || user.role === "admin");
  const insertableFields = fields.filter((f) => f.key !== nameKey);
  const defaultInsertField = (
    fields.find((f) => DESC_KEYS.includes(f.key)) || insertableFields[0] || { key: "description" }
  ).key;

  const toggleLearned = useCallback(async (id) => {
    try {
      const res = await api.post("/progress/toggle", { node_id: id });
      setLearnedIds((prev) => (res.data.learned ? [...prev, id] : prev.filter((x) => x !== id)));
      toast.success(res.data.learned ? "Marked as learned" : "Marked as not learned");
    } catch {
      toast.error("Please sign in to track progress.");
    }
  }, []);

  const saveResearchToNotes = async (text, sources) => {
    try {
      await api.post("/notes", { entry_type: "note", node_id: node.id, text });
      for (const s of (sources || []).slice(0, 6)) {
        if (s.url) await api.post("/notes", { entry_type: "link", node_id: node.id, url: s.url, label: s.title });
      }
      toast.success("Saved to your journal");
      setResearchOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save. Please sign in."); }
  };

  const openInsertPicker = (text) => { setInsertFieldKey(defaultInsertField); setInsertText(text); };
  const confirmInsert = async () => {
    try {
      const res = await api.post(`/kg/nodes/${node.id}/append-field`, { field: insertFieldKey, text: insertText });
      toast.success(res.data.staged ? "Added as a pending edit — approve it in the Review Queue" : "Inserted into this entity");
      setInsertText(null);
      setResearchOpen(false);
      if (!res.data.staged) setRefresh((x) => x + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not insert"); }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="hub-loading">
        <Loader2 className="w-7 h-7 text-[#c23c28] animate-spin" />
      </div>
    );
  }
  if (notFound || !node) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="hub-not-found">
        <p className="font-serif text-2xl text-[#14140f]">This entity could not be found.</p>
        <Button variant="outline" onClick={() => onOpenNode(null)} data-testid="hub-back-home">
          Back to doorways
        </Button>
      </div>
    );
  }

  const learned = learnedIds.includes(node.id);
  const descText = descField ? data[descField.key] : "";

  return (
    <div className="relative flex-1 overflow-y-auto" data-testid="connections-hub">
      {/* Sticky nav */}
      <div className="sticky top-0 z-30 bg-[#fdfdfb]/80 backdrop-blur-md border-b border-[#14140f]/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-[#4e6073] flex items-center gap-1.5 min-w-0" data-testid="hub-breadcrumb">
            <button
              onClick={() => onOpenNode(null)}
              data-testid="hub-breadcrumb-home"
              className="hover:text-[#14140f] transition-colors shrink-0"
            >
              Explore
            </button>
            <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[#14140f]/30" />
            <span className="shrink-0 hidden sm:inline">{ENTITY_LABELS[type] || type}</span>
            <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[#14140f]/30 hidden sm:inline" />
            <span className="text-[#14140f] font-medium truncate">{node.label}</span>
          </div>
          <button
            onClick={() => setSearchParams({ view: "map", node: node.id })}
            data-testid="hub-view-map"
            className="shrink-0 inline-flex items-center gap-2 bg-[#f5f4f0] text-[#14140f] border border-[#14140f]/20 hover:bg-[#14140f] hover:text-[#fdfdfb] px-4 py-2 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#10a37f]"
          >
            <MapIcon className="w-4 h-4" strokeWidth={1.75} /> View on Map
          </button>
        </div>
      </div>

      <motion.div
        key={node.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-8 lg:pt-12"
      >
        {/* Hero */}
        <span
          className="inline-block text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full mb-4"
          style={{ background: ENTITY_COLORS[type], color: ENTITY_TEXT[type] }}
          data-testid="hub-type-badge"
        >
          {ENTITY_LABELS[type] || type}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-[#14140f] leading-[1.05]" data-testid="hub-title">
          {node.label}
        </h1>
        {descText && (
          <div className="mt-5 max-w-3xl">
            {descField?.type === "markdown" ? (
              <Markdown className="text-lg text-[#4e6073] leading-relaxed">{descText}</Markdown>
            ) : (
              <p className="text-base sm:text-lg text-[#4e6073] leading-relaxed whitespace-pre-line">{descText}</p>
            )}
          </div>
        )}

        {/* CTAs */}
        <div className="flex flex-wrap items-center gap-2.5 mt-8">
          <Button
            onClick={() => (user ? toggleLearned(node.id) : navigate("/journey"))}
            data-testid="hub-toggle-learned"
            className={learned ? "bg-[#ff7360] hover:bg-[#e85a46] text-[#14140f]" : "bg-[#14140f] hover:bg-[#000] text-[#fdfdfb]"}
          >
            {learned ? "Learned ✓" : "Mark as learned"}
          </Button>
          {user && (
            <Button
              onClick={() => setResearchOpen(true)}
              data-testid="hub-research-btn"
              className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f]"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Research with AI
            </Button>
          )}
          {user && (
            <Button
              variant="outline"
              onClick={() => setSuggestOpen(true)}
              data-testid="hub-suggest-entity-btn"
              className="border-[#ff7360]/40 text-[#c23c28] hover:bg-[#ff7360]/10"
            >
              <Lightbulb className="w-4 h-4 mr-1.5" /> Suggest a new entity
            </Button>
          )}
        </div>

        {/* Start a learning journey — knowledge systems only */}
        {type === "knowledge_system" && (
          <button
            onClick={() => (user ? navigate(`/journey/path/${node.id}`) : navigate("/journey"))}
            data-testid="hub-start-journey"
            className="group mt-6 w-full text-left bg-gradient-to-br from-[#ff7360] to-[#e85a46] text-[#14140f] rounded-2xl p-6 sm:p-7 flex items-center justify-between gap-4 shadow-sm hover:shadow-xl transition-[box-shadow] duration-300 focus:outline-none focus:ring-2 focus:ring-[#14140f]"
          >
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#14140f]/70 mb-1.5">
                Guided path
              </div>
              <div className="font-serif text-xl sm:text-2xl">Start a learning journey through {node.label}</div>
              <p className="text-sm text-[#14140f]/70 mt-1.5 max-w-xl">
                Follow its schools and core concepts step by step, and track what you've mastered.
              </p>
            </div>
            <span className="shrink-0 w-11 h-11 rounded-full bg-[#14140f]/10 flex items-center justify-center group-hover:bg-[#14140f]/20 transition-colors">
              <ArrowRight className="w-5 h-5" />
            </span>
          </button>
        )}

        {/* Body fields */}
        {bodyFields.length > 0 && (
          <div className="mt-12">
            <NodeFields bodyFields={bodyFields} data={data} />
          </div>
        )}

        {/* Relationships grouped by relationship type */}
        {groups.length > 0 && (
          <div className="mt-14 flex flex-col gap-8" data-testid="hub-relationships">
            {groups.map(([label, items]) => (
              <div key={label}>
                <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[#0a7d5f] mb-3">
                  {label} <span className="text-[#4e6073] font-sans normal-case tracking-normal">· {items.length}</span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {items.map((it) => (
                    <button
                      key={it.edgeId}
                      onClick={() => onOpenNode(it.id)}
                      data-testid={`hub-rel-${it.edgeId}`}
                      title={PLURAL[it.otype] || it.otype}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#10a37f]"
                      style={{ background: ENTITY_COLORS[it.otype], color: ENTITY_TEXT[it.otype] }}
                    >
                      {it.label}
                      {it.stance && (
                        <span className="text-[10px] uppercase tracking-wide opacity-70">{it.stance}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {groups.length === 0 && bodyFields.length === 0 && !descText && (
          <p className="mt-12 text-[#14140f]/45 italic">No further detail recorded yet.</p>
        )}

        {/* Notes / learner actions */}
        <div className="mt-12 border-t border-[#14140f]/10 pt-8">
          <LearnerActions node={node} learned={learned} onToggleLearned={toggleLearned} />
        </div>
      </motion.div>

      <ResearchPanel
        open={researchOpen}
        onOpenChange={setResearchOpen}
        contextLabel={node.label}
        onSaveToNotes={saveResearchToNotes}
        onInsertEntity={isCurator ? openInsertPicker : undefined}
        enableExtract={isCurator}
        presetPrompt={`Tell me about ${node.label} in ancient Indian philosophy, with sources.`}
      />

      <SuggestEntityDialog open={suggestOpen} onOpenChange={setSuggestOpen} />

      <Dialog open={!!insertText} onOpenChange={(o) => !o && setInsertText(null)}>
        <DialogContent className="max-w-md" data-testid="insert-field-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Insert into which field?</DialogTitle>
            <DialogDescription>
              The research will be appended to the chosen field of <b className="text-foreground">{node.label}</b>.
              Edits to published entities are staged for review.
            </DialogDescription>
          </DialogHeader>
          <Select value={insertFieldKey} onValueChange={setInsertFieldKey}>
            <SelectTrigger data-testid="insert-field-select"><SelectValue placeholder="Choose a field…" /></SelectTrigger>
            <SelectContent>
              {insertableFields.map((f) => (
                <SelectItem key={f.key} value={f.key} data-testid={`insert-field-${f.key}`}>
                  {f.label || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsertText(null)}>Cancel</Button>
            <Button className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f]" onClick={confirmInsert}
              disabled={!insertFieldKey} data-testid="insert-field-confirm">
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
